import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  listPendingInvites,
  sendClientInvite,
  type PendingInvite,
} from "@/lib/adminUserAccess";
import { Copy, Loader2, MailPlus } from "lucide-react";

function shortDate(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Welcome links that are still open, with a way to send or copy each one. */
export function PendingInvites({ refreshKey = 0 }: { refreshKey?: number }) {
  const { toast } = useToast();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvites(await listPendingInvites());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const resend = async (invite: PendingInvite) => {
    setSending(invite.token);
    try {
      const res = await sendClientInvite({
        email: invite.email,
        first_name: invite.first_name || undefined,
        last_name: invite.last_name || undefined,
        una_amount: invite.una_amount || undefined,
        monthly_amount: invite.monthly_amount || undefined,
      });
      toast({
        title: res.success && res.sent ? "Invitation sent" : "Link ready",
        description: res.message,
        variant: res.success ? undefined : "destructive",
      });
      await load();
    } finally {
      setSending(null);
    }
  };

  if (!loading && invites.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Pending invites {invites.length > 0 && `(${invites.length})`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading</p>
        ) : (
          invites.map((invite) => {
            const name = [invite.first_name, invite.last_name]
              .filter(Boolean)
              .join(" ")
              .trim();
            return (
              <div
                key={invite.token}
                className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {name || invite.email}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {invite.email}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Invited {shortDate(invite.created_at)}, expires{" "}
                    {shortDate(invite.expires_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={sending === invite.token}
                    onClick={() => void resend(invite)}
                  >
                    {sending === invite.token ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <MailPlus className="h-4 w-4 mr-2" />
                    )}
                    Send email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void navigator.clipboard.writeText(invite.url);
                      toast({ title: "Link copied" });
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy link
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
